import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Clock,
  Calendar,
  Twitter,
  Facebook,
  Linkedin,
  Link as LinkIcon,
  List,
  Heart,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import SEO, { articleJsonLd, breadcrumbJsonLd } from "@/components/SEO";
import SubscribeForm from "@/components/SubscribeForm";
import { ABOUT, BRAND } from "@/lib/content";

/* ---------- helpers ---------- */
function readingTime(content = "") {
  const words = content.trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

function slugify(s = "") {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");
}

/* Parse plain-text-with-markdown content into a structured block list. */
function parseContent(raw = "") {
  const lines = raw.split(/\n/);
  const blocks = [];
  let para = [];
  let list = null;

  const flushPara = () => {
    if (para.length) {
      blocks.push({ type: "p", text: para.join(" ").trim() });
      para = [];
    }
  };
  const flushList = () => {
    if (list && list.items.length) blocks.push(list);
    list = null;
  };

  lines.forEach((line) => {
    const l = line.trim();
    if (!l) {
      flushPara();
      flushList();
      return;
    }
    if (l.startsWith("## ")) {
      flushPara();
      flushList();
      const text = l.slice(3).trim();
      blocks.push({ type: "h2", text, id: slugify(text) });
    } else if (l.startsWith("### ")) {
      flushPara();
      flushList();
      const text = l.slice(4).trim();
      blocks.push({ type: "h3", text, id: slugify(text) });
    } else if (l.startsWith("> ")) {
      flushPara();
      flushList();
      blocks.push({ type: "quote", text: l.slice(2).trim() });
    } else if (l.startsWith("- ") || l.startsWith("* ")) {
      flushPara();
      if (!list) list = { type: "ul", items: [] };
      list.items.push(l.slice(2).trim());
    } else {
      flushList();
      para.push(l);
    }
  });
  flushPara();
  flushList();
  return blocks;
}

/* ---------- Page ---------- */
export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    window.scrollTo({ top: 0, behavior: "instant" });
    api
      .get(`/blog/posts/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    if (!post) return;
    api
      .get("/blog/posts")
      .then((r) => {
        const all = r.data || [];
        const sameCat = all.filter(
          (p) => p.slug !== post.slug && p.category === post.category
        );
        const fillers = all.filter(
          (p) => p.slug !== post.slug && p.category !== post.category
        );
        setRelated([...sameCat, ...fillers].slice(0, 3));
      })
      .catch(() => {});
  }, [post]);

  const blocks = useMemo(() => (post ? parseContent(post.content) : []), [post]);
  const headings = useMemo(
    () => blocks.filter((b) => b.type === "h2" || b.type === "h3"),
    [blocks]
  );
  const minutes = post ? readingTime(post.content) : 0;

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <p className="eyebrow mb-3">404</p>
        <h1 className="font-serif-display text-4xl text-[#3A3A3A] mb-6">
          Post not <span className="italic text-[#E8A6B8]">found</span>
        </h1>
        <Link to="/blog" className="btn-pink mt-4" data-testid="blog-404-back">
          Back To The Journal
        </Link>
      </div>
    );
  }
  if (!post) {
    return <div className="text-center py-32 text-[#5C5C5C]">Loading…</div>;
  }

  const shareUrl =
    typeof window !== "undefined"
      ? window.location.href
      : `https://herfithub.com/blog/${post.slug}`;

  return (
    <div data-testid="blog-post-page" className="bg-[#F6EFE9] overflow-x-hidden">
      <SEO
        title={`${post.title} | HerFitHub Journal`}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
        image={post.image_url}
        type="article"
        publishedAt={post.published_at}
        modifiedAt={post.published_at}
        keywords={`${post.category}, women's strength coach London, ${post.title.toLowerCase()}, women's strength training, women's health, online coaching for women`}
        jsonLd={[
          articleJsonLd(post),
          breadcrumbJsonLd([
            { name: "Home", url: "/" },
            { name: "Journal", url: "/blog" },
            { name: post.title, url: `/blog/${post.slug}` },
          ]),
        ]}
      />
      <Hero post={post} minutes={minutes} />
      <FeaturedImage post={post} />
      <ArticleBody
        post={post}
        blocks={blocks}
        headings={headings}
        shareUrl={shareUrl}
      />
      <BookingCTA />
      <AuthorCard />
      <Related posts={related} />
      <NewsletterSection />
    </div>
  );
}

/* ---------- HERO ---------- */
function Hero({ post, minutes }) {
  return (
    <section className="relative overflow-hidden">
      <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-12 lg:pt-16 pb-8">
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] font-semibold text-[#3A3A3A] hover:text-[#E8A6B8] mb-10 transition"
          data-testid="back-to-blog"
        >
          <ArrowLeft size={14} /> Back To The Journal
        </Link>
        <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#E8A6B8]">
          {post.category}
        </p>
        <h1
          className="mt-3 font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#3A3A3A] leading-[1.05]"
          data-testid="article-title"
        >
          {post.title}
        </h1>
        <p className="mt-5 text-base sm:text-lg text-[#5C5C5C] leading-relaxed">
          {post.excerpt}
        </p>
        <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] uppercase tracking-[0.16em] text-[#5C5C5C]">
          <span className="flex items-center gap-2">
            <Calendar size={12} className="text-[#E8A6B8]" />
            {formatDate(post.published_at)}
          </span>
          <span className="text-[#E8A6B8]">•</span>
          <span className="flex items-center gap-2">
            <Clock size={12} className="text-[#E8A6B8]" />
            {minutes} min read
          </span>
          <span className="text-[#E8A6B8]">•</span>
          <span>By {post.author || "Maya"}</span>
        </div>
      </div>
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full bg-[#F3C9D3]/30 blur-3xl"
      />
    </section>
  );
}

/* ---------- FEATURED IMAGE ---------- */
function FeaturedImage({ post }) {
  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 pb-8">
      <figure className="relative rounded-[2rem] overflow-hidden bg-[#F3C9D3]/30 shadow-[0_24px_60px_rgba(232,166,184,0.18)]">
        <img
          src={post.image_url}
          alt={`${post.title} — ${post.category} · HerFitHub Journal`}
          loading="eager"
          decoding="async"
          className="w-full h-auto aspect-[16/9] object-cover"
          data-testid="article-image"
        />
      </figure>
    </div>
  );
}

/* ---------- ARTICLE BODY ---------- */
function ArticleBody({ post, blocks, headings, shareUrl }) {
  return (
    <section className="py-12 lg:py-16">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* Sidebar TOC + share */}
        <aside className="lg:col-span-3 order-2 lg:order-1">
          <div className="lg:sticky lg:top-24 space-y-6">
            {headings.length > 0 && <TOC headings={headings} />}
            <ShareBox url={shareUrl} title={post.title} />
          </div>
        </aside>

        {/* Article content */}
        <article className="lg:col-span-9 order-1 lg:order-2">
          <div
            className="prose-editorial"
            data-testid="article-body"
          >
            {blocks.map((b, i) => (
              <Block key={i} block={b} index={i} />
            ))}
          </div>
        </article>
      </div>

      <style>{`
        .prose-editorial p,
        .prose-editorial ul,
        .prose-editorial blockquote {
          font-family: "Manrope", sans-serif;
          font-size: 1.05rem;
          line-height: 1.85;
          color: #3A3A3A;
        }
        @media (min-width: 640px) {
          .prose-editorial p,
          .prose-editorial ul,
          .prose-editorial blockquote { font-size: 1.125rem; }
        }
        .prose-editorial p { margin-bottom: 1.4rem; }
        .prose-editorial p.lead::first-letter {
          font-family: "Cormorant Garamond", serif;
          font-size: 4.2rem;
          font-weight: 500;
          float: left;
          line-height: 0.9;
          margin: 0.3rem 0.6rem 0 0;
          color: #E8A6B8;
        }
        .prose-editorial h2 {
          font-family: "Cormorant Garamond", serif;
          font-weight: 500;
          font-size: 2rem;
          color: #3A3A3A;
          margin: 2.6rem 0 1rem;
          letter-spacing: -0.01em;
          scroll-margin-top: 110px;
        }
        @media (min-width: 640px) {
          .prose-editorial h2 { font-size: 2.4rem; }
        }
        .prose-editorial h3 {
          font-family: "Cormorant Garamond", serif;
          font-weight: 500;
          font-size: 1.55rem;
          color: #3A3A3A;
          margin: 2rem 0 0.8rem;
          letter-spacing: -0.01em;
          scroll-margin-top: 110px;
        }
        .prose-editorial blockquote {
          margin: 2rem 0;
          padding: 1.6rem 1.8rem;
          background: linear-gradient(135deg, #F6EFE9 0%, #F3C9D3 100%);
          border-radius: 1.5rem;
          font-family: "Cormorant Garamond", serif;
          font-style: italic;
          font-size: 1.4rem;
          color: #3A3A3A;
          line-height: 1.5;
          position: relative;
          border: 1px solid rgba(232,166,184,0.3);
        }
        .prose-editorial ul {
          padding-left: 0;
          margin: 1.4rem 0;
          list-style: none;
        }
        .prose-editorial ul li {
          position: relative;
          padding-left: 1.7rem;
          margin-bottom: 0.65rem;
        }
        .prose-editorial ul li::before {
          content: "";
          position: absolute;
          left: 0;
          top: 0.7rem;
          width: 8px;
          height: 8px;
          border-radius: 9999px;
          background: #E8A6B8;
        }
        .prose-editorial strong { color: #3A3A3A; font-weight: 600; }
        .prose-editorial em { font-style: italic; color: #3A3A3A; }
      `}</style>
    </section>
  );
}

function Block({ block, index }) {
  if (block.type === "h2")
    return (
      <h2 id={block.id} data-testid={`article-h2-${block.id}`}>
        {block.text}
      </h2>
    );
  if (block.type === "h3")
    return (
      <h3 id={block.id} data-testid={`article-h3-${block.id}`}>
        {block.text}
      </h3>
    );
  if (block.type === "quote")
    return <blockquote data-testid={`article-quote-${index}`}>{block.text}</blockquote>;
  if (block.type === "ul")
    return (
      <ul data-testid={`article-list-${index}`}>
        {block.items.map((it, j) => (
          <li key={j}>{it}</li>
        ))}
      </ul>
    );
  // paragraph — first paragraph gets drop cap
  return (
    <p className={index === 0 ? "lead" : ""} data-testid={`article-p-${index}`}>
      {block.text}
    </p>
  );
}

/* ---------- TOC ---------- */
function TOC({ headings }) {
  return (
    <nav
      aria-label="Table of contents"
      className="bg-white rounded-[1.5rem] border border-[#F3C9D3]/40 p-5"
      data-testid="article-toc"
    >
      <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#3A3A3A] mb-3 flex items-center gap-2">
        <List size={12} className="text-[#E8A6B8]" /> In This Article
      </p>
      <ul className="space-y-2">
        {headings.map((h) => (
          <li key={h.id}>
            <a
              href={`#${h.id}`}
              className={`block text-xs leading-snug text-[#5C5C5C] hover:text-[#E8A6B8] transition ${
                h.type === "h3" ? "pl-3" : ""
              }`}
              data-testid={`toc-link-${h.id}`}
            >
              {h.text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/* ---------- SHARE BOX ---------- */
function ShareBox({ url, title }) {
  const enc = encodeURIComponent;
  const tw = `https://twitter.com/intent/tweet?text=${enc(title)}&url=${enc(url)}`;
  const fb = `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`;
  const li = `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Could not copy link");
    }
  };

  const btnCls =
    "inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#F6EFE9] hover:bg-[#F3C9D3]/60 border border-[#F3C9D3]/40 text-[#3A3A3A] hover:text-[#E8A6B8] transition";

  return (
    <div
      className="bg-white rounded-[1.5rem] border border-[#F3C9D3]/40 p-5"
      data-testid="article-share"
    >
      <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#3A3A3A] mb-3">
        Share This Article
      </p>
      <div className="flex items-center gap-2">
        <a href={tw} target="_blank" rel="noreferrer" aria-label="Share on X" className={btnCls} data-testid="share-twitter">
          <Twitter size={14} strokeWidth={1.6} />
        </a>
        <a href={fb} target="_blank" rel="noreferrer" aria-label="Share on Facebook" className={btnCls} data-testid="share-facebook">
          <Facebook size={14} strokeWidth={1.6} />
        </a>
        <a href={li} target="_blank" rel="noreferrer" aria-label="Share on LinkedIn" className={btnCls} data-testid="share-linkedin">
          <Linkedin size={14} strokeWidth={1.6} />
        </a>
        <button
          type="button"
          onClick={copy}
          aria-label="Copy link"
          className={btnCls}
          data-testid="share-copy"
        >
          <LinkIcon size={14} strokeWidth={1.6} />
        </button>
      </div>
    </div>
  );
}

/* ---------- INLINE BOOKING CTA ---------- */
function BookingCTA() {
  return (
    <section className="px-6 lg:px-10 pt-8 pb-16">
      <div className="max-w-4xl mx-auto">
        <div
          className="relative overflow-hidden rounded-[2rem] border border-[#F3C9D3]/50 bg-gradient-to-br from-white via-[#F6EFE9] to-[#F3C9D3]/40 px-7 sm:px-12 py-10 sm:py-12 shadow-[0_18px_44px_rgba(232,166,184,0.16)]"
          data-testid="article-booking-cta"
        >
          <div
            aria-hidden
            className="pointer-events-none absolute -top-20 -right-16 w-72 h-72 rounded-full bg-[#E8A6B8]/15 blur-3xl"
          />
          <div className="relative grid sm:grid-cols-12 gap-6 items-center">
            <div className="sm:col-span-9">
              <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#E8A6B8]">
                Want Personal Guidance?
              </p>
              <h3 className="font-serif-display text-3xl sm:text-4xl text-[#3A3A3A] leading-tight mt-2">
                One-to-one <span className="italic text-[#E8A6B8]">women's strength coaching</span>
              </h3>
              <p className="mt-3 text-sm sm:text-base text-[#5C5C5C] leading-relaxed max-w-xl">
                If this article resonated, you might love working with Maya
                directly. Online coaching for women, postpartum strength
                coaching and beginner-friendly support — designed around real life.
              </p>
            </div>
            <div className="sm:col-span-3 flex sm:justify-end">
              <Link
                to="/booking"
                className="btn-pink w-full sm:w-auto"
                data-testid="article-booking-link"
              >
                Book Consultation <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- AUTHOR CARD ---------- */
function AuthorCard() {
  return (
    <section className="bg-white py-16 lg:py-20">
      <div className="max-w-4xl mx-auto px-6 lg:px-10">
        <article className="grid sm:grid-cols-12 gap-6 sm:gap-8 items-center bg-[#F6EFE9] rounded-[2rem] border border-[#F3C9D3]/40 p-6 sm:p-8">
          <div className="sm:col-span-3">
            <div className="relative rounded-[1.5rem] overflow-hidden aspect-square bg-[#F3C9D3]/40">
              <img
                src={ABOUT.image}
                alt="Maya — women's strength coach London"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
          </div>
          <div className="sm:col-span-9">
            <p className="eyebrow mb-2 flex items-center gap-2">
              <Sparkles size={12} className="text-[#E8A6B8]" /> About The Author
            </p>
            <h3 className="font-serif-display text-2xl sm:text-3xl text-[#3A3A3A] leading-tight">
              Maya · <span className="italic text-[#E8A6B8]">HerFitHub</span>
            </h3>
            <p className="mt-3 text-sm text-[#5C5C5C] leading-relaxed">
              London-based women's strength coach helping beginners, postpartum mums
              and returning lifters build strength, confidence and trust in their
              bodies. Written from coaching real women, every week.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <Link to="/about" className="btn-outline text-xs" data-testid="article-author-about">
                Meet Maya <ArrowRight size={12} />
              </Link>
              {BRAND.instagram?.[0] && (
                <a
                  href={BRAND.instagram[0].url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#3A3A3A] hover:text-[#E8A6B8] transition"
                >
                  {BRAND.instagram[0].handle}
                </a>
              )}
            </div>
          </div>
        </article>
      </div>
    </section>
  );
}

/* ---------- RELATED POSTS ---------- */
function Related({ posts }) {
  if (!posts || posts.length === 0) return null;
  return (
    <section className="py-16 lg:py-24 bg-[#F3C9D3]/20">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-end justify-between gap-6 mb-10">
          <div>
            <p className="eyebrow mb-2 flex items-center gap-2">
              <Heart size={11} className="text-[#E8A6B8]" /> Keep Reading
            </p>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-[#3A3A3A]">
              Related <span className="italic text-[#E8A6B8]">articles</span>
            </h2>
          </div>
          <Link
            to="/blog"
            className="hidden sm:inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] font-semibold text-[#3A3A3A] hover:text-[#E8A6B8] transition"
            data-testid="related-all-link"
          >
            All Articles <ArrowRight size={13} />
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {posts.map((p) => (
            <Link
              key={p.id}
              to={`/blog/${p.slug}`}
              className="group bg-white rounded-[2rem] overflow-hidden border border-[#F3C9D3]/40 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_rgba(232,166,184,0.2)] flex flex-col"
              data-testid={`related-post-${p.slug}`}
            >
              <div className="aspect-[4/3] overflow-hidden bg-[#F3C9D3]/20">
                <img
                  src={p.image_url}
                  alt={`${p.title} — ${p.category}`}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <p className="text-[10.5px] uppercase tracking-[0.18em] font-semibold text-[#E8A6B8]">
                  {p.category}
                </p>
                <h3 className="font-serif-display text-xl text-[#3A3A3A] leading-snug mt-2">
                  {p.title}
                </h3>
                <p className="mt-3 text-xs text-[#5C5C5C] leading-relaxed line-clamp-2 flex-1">
                  {p.excerpt}
                </p>
                <span className="mt-4 inline-flex items-center gap-2 text-[10.5px] uppercase tracking-[0.16em] font-semibold text-[#3A3A3A] group-hover:text-[#E8A6B8] transition">
                  <Clock size={11} className="text-[#E8A6B8]" /> {readingTime(p.content)} min read
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- NEWSLETTER ---------- */
function NewsletterSection() {
  return (
    <section className="py-20 lg:py-24 bg-white">
      <div className="max-w-4xl mx-auto px-6 lg:px-10">
        <SubscribeForm
          source="blog-article"
          title="Get the next article delivered with care"
          body="Soft inbox, never spam. Fresh writing on women's strength training, women's health and gym confidence — straight from Maya."
        />
      </div>
    </section>
  );
}
